import { holdsPermission, type Principal } from '../session/permissions.js';
import { configTopic, interactionTopic, ownAgentTopic, type Topic } from './frames.js';

/**
 * What this session asks to follow.
 *
 * The client subscribes to what its permissions suggest and no more (authorisation in the UI). That
 * is not the authorisation — the gateway authorises every topic and refuses the ones that are not
 * this principal's — but asking for a topic that will be refused costs an error frame on every
 * reconnection, and a workspace that asks for everything is a workspace nobody can read the logs of.
 *
 * `agent:me` is sent as the word, not as an id: the gateway resolves it to the token's own agent and
 * answers with it resolved, which is how the client comes to know its own agent id at all.
 */
export function topicsFor(
  principal: Principal | undefined,
  followedInteractions: readonly string[] = [],
): Topic[] {
  const topics: Topic[] = [];
  if (principal === undefined) {
    return topics;
  }
  // Everyone authenticated: a client whose configuration is stale shows the wrong queues and the
  // wrong dispositions, and "the configuration changed" is not private.
  topics.push(configTopic);
  // `agents.activity` is the permission an agent holds over their own activity, so it is the one
  // that says this user has an agent to follow at all.
  if (holdsPermission(principal, 'agents.activity')) {
    topics.push(ownAgentTopic);
  }
  for (const interactionId of followedInteractions) {
    topics.push(interactionTopic(interactionId));
  }
  return topics;
}
